import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ViewStyle,
  Text,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

interface StarRatingProps {
  rating: number;
  onRatingChange: (rating: number) => void;
  maxStars?: number;
  size?: number;
  disabled?: boolean;
  style?: ViewStyle;
  showLabel?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  onRatingChange,
  maxStars = 5,
  size = 32,
  disabled = false,
  style,
  showLabel = true,
}) => {
  const { colors } = useTheme();

  const handleStarPress = (starIndex: number) => {
    if (!disabled) {
      onRatingChange(starIndex + 1);
    }
  };

  const renderStars = () => {
    return Array.from({ length: maxStars }, (_, index) => {
      const starValue = index + 1;
      const isFilled = starValue <= rating;
      const _isHalf = false; // Can be extended for half-star support

      return (
        <TouchableOpacity
          key={index}
          onPress={() => handleStarPress(index)}
          disabled={disabled}
          activeOpacity={0.7}
          hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
          style={[styles.starButton, index > 0 && { marginLeft: 4 }]}
        >
          <Ionicons
            name={isFilled ? 'star' : 'star-outline'}
            size={size}
            color={isFilled ? colors.primary : colors.border}
            style={styles.star}
          />
        </TouchableOpacity>
      );
    });
  };

  const getRatingLabel = () => {
    if (rating === 0) return 'Not rated';
    if (rating === 1) return 'Poor';
    if (rating === 2) return 'Fair';
    if (rating === 3) return 'Good';
    if (rating === 4) return 'Very Good';
    if (rating === 5) return 'Excellent';
    return '';
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.starsContainer}>{renderStars()}</View>
      {showLabel && rating > 0 && (
        <View style={styles.labelContainer}>
          <View
            style={[
              styles.ratingBadge,
              { backgroundColor: colors.primary + '20' },
            ]}
          >
            <Text style={[styles.ratingText, { color: colors.primary }]}>
              {rating} / {maxStars}
            </Text>
          </View>
          <Text
            style={[
              styles.labelText,
              { color: colors.secondary, marginLeft: 8 },
            ]}
          >
            {getRatingLabel()}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
  },
  starsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  starButton: {
    padding: 4,
  },
  star: {
    // Star icon styling
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  ratingBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  ratingText: {
    fontSize: 12,
    fontWeight: '600',
  },
  labelText: {
    fontSize: 12,
    fontWeight: '500',
  },
});

export default StarRating;
